import type { FleetCompositionBundle } from '../../services/fleetCompositionService';
import type { FleetCompositionMetrics } from '../../services/fleetCompositionService';
import {
  FLEET_CLOSEOUT,
  fleetNarrativeViewId,
  renderCloseoutBundle,
} from './closeoutTransportCopy';
import type { NarrativeTokenMap } from './narrativeTemplateEngine';
import {
  buildFleetClassCategoryFacts,
  buildFleetDutyClassFacts,
  buildFleetFuelFacts,
  buildFleetIntensityFacts,
  type FleetScope,
} from './fleetCompositionFacts';

export function resolveFleetNarrativeViewId(
  chartView: 'intensity' | 'by_class' | 'by_fuel',
  fuelScope: FleetScope,
  classMode: 'vehicle_category' | 'class'
): string {
  return fleetNarrativeViewId(chartView, fuelScope, classMode);
}

export function buildFleetCompositionAuthoredBundle(
  locationName: string,
  bundle: FleetCompositionBundle | null,
  chartView: 'intensity' | 'by_class' | 'by_fuel',
  fuelScope: FleetScope,
  classMode: 'vehicle_category' | 'class'
) {
  const viewId = resolveFleetNarrativeViewId(chartView, fuelScope, classMode);
  const block = FLEET_CLOSEOUT[viewId];
  if (!block) {
    return renderCloseoutBundle(
      FLEET_CLOSEOUT.by_fuel_ldv,
      { locationName },
      { chartId: 'vehicle_fleet_composition', viewId: 'default' }
    );
  }

  let tokens: Record<string, unknown> = { locationName };
  if (viewId === 'by_fuel_ldv') {
    tokens = buildFleetFuelFacts(locationName, bundle, 'LDV');
  } else if (viewId === 'by_fuel_all') {
    tokens = buildFleetFuelFacts(locationName, bundle, 'all');
  } else if (viewId === 'by_class_vehicle_category') {
    tokens = buildFleetClassCategoryFacts(locationName, bundle);
  } else if (viewId === 'by_class_duty_class') {
    tokens = buildFleetDutyClassFacts(locationName, bundle);
  } else if (viewId === 'intensity') {
    tokens = buildFleetIntensityFacts(locationName, bundle, bundle?.metrics ?? null);
  }

  return renderCloseoutBundle(block, tokens as NarrativeTokenMap, {
    chartId: 'vehicle_fleet_composition',
    viewId: 'default',
  });
}

export function buildFleetCompositionKeyInsights(
  locationName: string,
  bundle: FleetCompositionBundle | null,
  chartView: 'intensity' | 'by_class' | 'by_fuel',
  fuelScope: FleetScope,
  classMode: 'vehicle_category' | 'class'
): string {
  const authored = buildFleetCompositionAuthoredBundle(
    locationName,
    bundle,
    chartView,
    fuelScope,
    classMode
  );
  return authored.keyInsights || `Fleet composition data are not available for ${locationName}.`;
}

export function fleetCompositionOverviewDescription(
  chartView: 'intensity' | 'by_class' | 'by_fuel',
  fuelScope: FleetScope,
  classMode: 'vehicle_category' | 'class'
): string {
  const viewId = resolveFleetNarrativeViewId(chartView, fuelScope, classMode);
  return FLEET_CLOSEOUT[viewId]?.overview ?? 'Registered vehicle fleet composition.';
}

export function fleetCompositionStructuredMethodology() {
  return {
    primarySources: [
      'California Air Resources Board (CARB) EMFAC Fleet Database — registration-derived vehicle population by fuel and vehicle class.',
    ],
    calculation: [
      'Aggregate EMFAC fleet strata to the selected city or county for each reporting year.',
      'Fuel and class stacks sum vehicle_population; plug-in share uses battery-electric and plug-in hybrid counts over the displayed fleet denominator.',
      'LDV emissions intensity (gCO₂e/mile) is VMT-weighted from EMFAC fleet strata when shown on the intensity view.',
    ],
    limitations: [
      'Fleet population is not VMT; intensity methodology requires verified emissions scope (NEEDS_SOURCE_VERIFICATION for full intensity publication).',
    ],
    technicalDetails: ['emfac2025_fleet_mat', 'fleetCompositionService.ts', 'FleetCompositionSection.tsx'],
  };
}

export type { FleetCompositionMetrics };
